// src/features/admin/components/AssessmentResultsManager.jsx
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "shared/lib/config";
import { Plus, Upload, Download, ChevronDown } from "lucide-react";
import DeleteConfirmModal from "./DeleteConfirmModal";
import { toast } from "react-toastify";
import DataTable from "./DataTable";
import AssessmentResultForm from "./AssessmentResultForm";
import AssessmentResultView from "./AsessmentResultView";
import BulkAssessmentImportSummaryModal from "./BulkAssessmentImportSummaryModal";
import BulkImportModal from "./BulkImport";

function getStore() {
  if (sessionStorage.getItem("idToken")) return sessionStorage;
  if (localStorage.getItem("idToken")) return localStorage;
  return localStorage;
}

export default function AssessmentResultsManager() {
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    title: "Konfirmasi",
    message: "",
    tone: "danger",
    confirmText: "Ya, Hapus",
    cancelText: "Batal",
    resolve: null,
  });

  const confirmDelete = useCallback(
    ({
      title = "Konfirmasi",
      message = "",
      tone = "danger",
      confirmText = "Ya, Hapus",
      cancelText = "Batal",
    } = {}) =>
      new Promise((resolve) => {
        setDeleteModal({
          open: true,
          title,
          message,
          tone,
          confirmText,
          cancelText,
          resolve,
        });
      }),
    []
  );

  const closeDeleteModal = useCallback((value) => {
    setDeleteModal((prev) => {
      prev.resolve?.(value);
      return { ...prev, open: false, resolve: null };
    });
  }, []);

  const navigate = useNavigate();
  const [distributorOpen, setDistributorOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const distributorDropdownRef = useRef(null);

useEffect(() => {
  const onClickOutside = (e) => {
    if (!distributorDropdownRef.current) return;
    if (!distributorDropdownRef.current.contains(e.target)) {
      setDistributorOpen(false);
    }
  };

  document.addEventListener("mousedown", onClickOutside);
  return () => document.removeEventListener("mousedown", onClickOutside);
}, []);

  const [showForm, setShowForm] = useState(false);
  const [editingResult, setEditingResult] = useState(null);
  const [viewingResult, setViewingResult] = useState(null);

  // filter distributor
  const [filterDistributor, setFilterDistributor] = useState("");
  const [importing, setImporting] = useState(false);
  const [sortOrder, setSortOrder] = useState("desc");

  // DataTable search
  const [tableSearch, setTableSearch] = useState("");

  // open/close bulk modal
  const [showBulkImport, setShowBulkImport] = useState(false);

  // summary modal
  const [importSummary, setImportSummary] = useState(null);
  const [showImportSummary, setShowImportSummary] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  /* ===========================
   * Load from backend
   * =========================*/
  const loadResults = useCallback(async () => {
    try {
      const store = getStore();
      const idToken = store.getItem("idToken");
      if (!idToken) {
        console.warn("[AssessmentResultsManager] idToken tidak ada");
        return;
      }

      setLoading(true);

      const params = new URLSearchParams();
      params.set("page", "1");
      params.set("limit", "200");

      const resp = await fetch(`${BASE_URL}/api/admin/assessments?${params.toString()}`, {
        headers: { Authorization: `Bearer ${idToken}` },
      });

      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        console.error("loadResults error:", resp.status, body);
        toast.error(body?.message || "Gagal memuat data hasil asesmen dari server.");
        return;
      }

      const body = await resp.json();

      const rows = (body?.data || []).map((r) => ({
        ...r,
        id: r.id || r._id,
        distributor_id: r.distributor_id || r.distributorId || r.distributor?.id || r.distributor?._id,
        distributorId: r.distributorId || r.distributor_id,
        school_id: r.school_id || r.schoolId || r.school?.id || r.school?._id,
        schoolId: r.schoolId || r.school_id,
        school_name: r.school_name || r.schoolName || r.school?.name || "",
        schoolName: r.schoolName || r.school_name,
        dob: r.dob || r.dateOfBirth || r.tanggal_lahir || r.tanggalLahir || "",
        tanggal_lahir: r.tanggal_lahir || r.tanggalLahir || r.dob || r.dateOfBirth || "",
        tanggalLahir: r.tanggalLahir || r.tanggal_lahir || r.dob || r.dateOfBirth || "",

      }));

      setResults(rows);
    } catch (err) {
      console.error("loadResults error:", err);
      toast.error("Terjadi kesalahan saat memuat data hasil asesmen.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadResults();
  }, [loadResults]);

  /* ===========================
   * Navigate to Report
   * =========================*/
  const gotoReport = (result) => {
    const voucher = result?.voucher || "";
    const search = voucher
      ? `?voucher=${encodeURIComponent(voucher)}`
      : result?.id || result?._id
      ? `?assessmentId=${encodeURIComponent(result.id || result._id)}`
      : "";

    const schoolName = result.school_name || result.schoolName || null;

    navigate(`/report${search}`, {
      state: {
        from: "admin-assessments",
        studentName: result?.test_taker_name || "",
        voucher: voucher || null,
        schoolName,
        school: schoolName,
        preloadedAssessment: {
          ...result,
          test_taker_name: result.test_taker_name,
          studentName: result.test_taker_name,

          strengths: result.strengths,
          learningStyles: result.learning_styles,
          extracurricularRecommendations: result.extracurricular_recommendations,
          MinatBakat: result.minat_bakat,

          schoolId: result.school_id || result.schoolId,
          schoolName,
          school: schoolName,

          classId: result.class_id,
          className: result.class_name,
          class: result.class_name,

          teacherId: result.teacher_id,
          teacherName: result.teacher_name,
          academicYearId: result.academic_year_id,
          academicYearName: result.academic_year_name,

          scores: result.scores || [],

          createdAt: result.created_at,
          createdAtVendor: result.created_at_vendor,
        },
      },
    });
  };

  /* ===========================
   * CRUD handlers
   * =========================*/
  const handleDelete = async (result) => {
    const ok = await confirmDelete({
      title: "Hapus hasil asesmen?",
      message: `Hapus hasil asesmen untuk "${result.test_taker_name}"?\nTindakan ini tidak dapat dibatalkan.`,
      tone: "danger",
      confirmText: "Ya, Hapus",
      cancelText: "Batal",
    });
    if (!ok) return;

    try {
      const store = getStore();
      const idToken = store.getItem("idToken");
      if (!idToken) {
        toast.info("Token login tidak ditemukan, silakan login ulang.");
        return;
      }

      const resp = await fetch(`${BASE_URL}/api/admin/assessments/${result.id || result._id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${idToken}` },
      });

      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        console.error("delete error:", resp.status, body);
        toast.error(body?.message || "Gagal menghapus hasil asesmen.");
        return;
      }

      toast.success(body?.message || "Hasil asesmen berhasil dihapus.");
      loadResults();
    } catch (err) {
      console.error("delete error:", err);
      toast.error("Terjadi kesalahan saat menghapus hasil asesmen.");
    }
  };

  const handleBulkDelete = async (selectedResults) => {
    if (!selectedResults?.length) return;

    const ok = await confirmDelete({
      title: "Hapus beberapa asesmen?",
      message: `Hapus ${selectedResults.length} hasil asesmen terpilih?\nTindakan ini tidak dapat dibatalkan.`,
      tone: "danger",
      confirmText: "Ya, Hapus",
      cancelText: "Batal",
    });
    if (!ok) return;

    try {
      const store = getStore();
      const idToken = store.getItem("idToken");
      if (!idToken) {
        toast.info("Token login tidak ditemukan, silakan login ulang.");
        return;
      }

      await Promise.all(
        selectedResults.map((r) =>
          fetch(`${BASE_URL}/api/admin/assessments/${r.id || r._id}`, {
            method: "DELETE",
            headers: { Authorization: `Bearer ${idToken}` },
          }).catch((err) => {
            console.error("bulk delete error for", r.id || r._id, err);
          })
        )
      );

      toast.success(`${selectedResults.length} hasil asesmen berhasil dihapus.`);
      loadResults();
    } catch (err) {
      console.error("bulk delete wrapper error:", err);
      toast.error("Terjadi kesalahan saat bulk delete.");
    }
  };

  const handleView = (result) => gotoReport(result);

  // ✅ IMPORTANT: keep old behavior so edit does NOT call GET /:id (prevents backend crash)
  const handleEdit = (result) => {
    const normalized = {
      ...result,
      id: result.id || result._id,
      distributor_id:
        result.distributor_id ||
        result.distributorId ||
        result.distributor?.id ||
        result.distributor?._id ||
        "",
      distributorId: result.distributorId || result.distributor_id || "",
      school_id: result.school_id || result.schoolId || result.school?.id || result.school?._id || "",
      schoolId: result.schoolId || result.school_id || "",
      school_name: result.school_name || result.schoolName || result.school?.name || "",
      schoolName: result.schoolName || result.school_name || "",
      dob:
  result?.dob ||
  result?.dateOfBirth ||
  result?.tanggal_lahir ||
  result?.tanggalLahir ||
  "",
tanggal_lahir:
  result?.tanggal_lahir ||
  result?.tanggalLahir ||
  result?.dob ||
  result?.dateOfBirth ||
  "",
tanggalLahir:
  result?.tanggalLahir ||
  result?.tanggal_lahir ||
  result?.dob ||
  result?.dateOfBirth ||
  "",

    };

    setEditingResult(normalized);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingResult(null);
  };

  const handleViewClose = () => {
    setViewingResult(null);
  };

  const handleSaved = () => {
    setShowForm(false);
    setEditingResult(null);
    loadResults();
  };

  /* ===========================
   * Table columns & filters
   * =========================*/
  const columns = [
    { key: "test_taker_name", label: "Nama" },
    { key: "gender", label: "Gender", render: (r) => r.gender || "-" },
    {
  key: "tanggal_lahir",
  label: "Tanggal Lahir",
  render: (r) => {
    const raw =
      r?.tanggal_lahir ||
      r?.tanggalLahir ||
      r?.dob ||
      r?.dateOfBirth ||
      null;

    if (!raw) return "-";

    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return "-";

    return d.toLocaleDateString("id-ID");
  },
},

    { key: "age", label: "Usia", render: (r) => (r.age ? `${r.age} tahun` : "-") },
    { key: "voucher", label: "Voucher" },
    { key: "distributor", label: "Distributor", render: (r) => r.distributor || "-" },
    {
      key: "school_name",
      label: "Sekolah",
      render: (r) => r.school_name || r.schoolName || r.school?.name || "-",
    },
    {
      key: "created_at",
      label: "Tanggal",
      render: (r) => {
        const raw = r.created_at_vendor || r.created_at;
        if (!raw) return "-";
        return new Date(raw).toLocaleDateString("id-ID");
      },
    },
  ];

  const uniqueDistributors = useMemo(
    () => Array.from(new Set(results.map((r) => r.distributor).filter(Boolean))).sort(),
    [results]
  );

  const filteredResults = useMemo(() => {
    const normDistributor = filterDistributor.trim().toLowerCase();
    const normSearch = tableSearch.trim().toLowerCase();

    const filtered = results.filter((r) => {
      if (normDistributor) {
        const dist = (r.distributor || "").toLowerCase();
        if (!dist.includes(normDistributor)) return false;
      }

      if (normSearch) {
        const name = (r.test_taker_name || "").toLowerCase();
        const voucher = (r.voucher || "").toLowerCase();
        const dist = (r.distributor || "").toLowerCase();
        const school = (r.school_name || r.schoolName || "").toLowerCase();

        const hay = `${name} ${voucher} ${dist} ${school}`;
        if (!hay.includes(normSearch)) return false;
      }

      return true;
    });

    const sorted = [...filtered].sort((a, b) => {
      const da = new Date(a.created_at_vendor || a.created_at || 0).getTime();
      const db = new Date(b.created_at_vendor || b.created_at || 0).getTime();
      return sortOrder === "desc" ? db - da : da - db;
    });

    return sorted;
  }, [results, filterDistributor, tableSearch, sortOrder]);

  const visibleRows = useMemo(() => {
    const p = Math.max(Number(page) || 1, 1);
    if (pageSize === "all") return filteredResults;

    const ps = Math.max(Number(pageSize) || 10, 1);
    const start = (p - 1) * ps;
    const end = start + ps;
    return filteredResults.slice(start, end);
  }, [filteredResults, page, pageSize]);

  const canExport = !loading && !importing && visibleRows.length > 0;

  const exportTooltip = !canExport
    ? loading
      ? "Sedang memuat data..."
      : importing
      ? "Sedang mengimpor, export dinonaktifkan."
      : "Tidak ada data yang bisa diexport. Ubah filter/pencarian atau pindah halaman."
    : "Export data sesuai tampilan tabel (halaman saat ini)";

  /* ===========================
   * Export Excel
   * =========================*/
  const handleExport = async () => {
    try {
      const store = getStore();
      const idToken = store.getItem("idToken");
      if (!idToken) {
        toast.info("Sesi login sudah habis. Silakan login ulang.");
        return;
      }

      if (!visibleRows.length) {
        toast.info("Tidak ada data pada halaman ini untuk diexport.");
        return;
      }

      const ids = visibleRows
        .map((r) => r.id || r._id)
        .filter(Boolean)
        .map(String);

      if (!ids.length) {
        toast.error("Data tidak punya id untuk diexport.");
        return;
      }

      const params = new URLSearchParams();
      params.set("ids", ids.join(","));

      const url = `${BASE_URL}/api/admin/assessments/export?${params.toString()}`;

      const res = await fetch(url, {
        method: "GET",
        headers: { Authorization: `Bearer ${idToken}` },
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const msg = body.message || `Gagal mengekspor hasil asesmen (status ${res.status}).`;
        toast.error(msg);
        return;
      }

      const blob = await res.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const today = new Date().toISOString().split("T")[0];

      a.href = downloadUrl;
      a.download = `hasil_asesmen_${today}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error("[AssessmentResultsManager] handleExport error:", err);
      toast.error("Terjadi kesalahan saat mengekspor hasil asesmen.");
    }
  };

  /* ===========================
   * Import (bulk-import)
   * =========================*/
  const handleBulkUploadFile = async (file) => {
    if (!file) return;

    setImporting(true);

    try {
      const store = getStore();
      const idToken = store.getItem("idToken");
      if (!idToken) {
        toast.info("Token login tidak ditemukan, silakan login ulang.");
        return;
      }

      const formData = new FormData();
      formData.append("file", file);

      const resp = await fetch(`${BASE_URL}/api/admin/assessments/bulk-import`, {
        method: "POST",
        headers: { Authorization: `Bearer ${idToken}` },
        body: formData,
      });

      const body = await resp.json().catch(() => ({}));

      if (!resp.ok) {
        console.error("[bulk-import] error:", resp.status, body);
        toast.error(body?.message || "Gagal mengimpor hasil asesmen. Periksa format file dan coba lagi.");
        return;
      }

      const rawSummary = body?.summary || {};
      const uiSummary = {
        totalRows: rawSummary.totalRows ?? 0,
        createdCount: rawSummary.createdCount ?? 0,
        updatedCount: rawSummary.updatedCount ?? 0,
        rowWarnings: (body?.rowWarnings || []).map((x) => ({
          rowNumber: x.rowNumber ?? x.row ?? null,
          voucher: x.voucher || "",
          reason: x.reason || x.message || "-",
          code: x.code,
        })),
        rowErrors: (body?.rowErrors || []).map((x) => ({
          rowNumber: x.rowNumber ?? x.row ?? null,
          voucher: x.voucher || "",
          reason: x.reason || x.message || "-",
          code: x.code,
        })),
        rowNotes: (body?.rowNotes || []).map((x) => ({
          rowNumber: x.rowNumber ?? x.row ?? null,
          voucher: x.voucher || "",
          reason: x.reason || x.message || "-",
          code: x.code,
        })),
      };

      setImportSummary(uiSummary);
      setShowImportSummary(true);

      setShowBulkImport(false);
      await loadResults();
    } catch (error) {
      console.error("Error importing assessment results:", error);
      toast.error("Terjadi kesalahan saat mengimpor data.");
    } finally {
      setImporting(false);
    }
  };

  const assessmentTemplate = useMemo(() => {
    const metricHeaders = [
      "Vocabulary",
      "Number Sequence",
      "Narration Recall",
      "Complex Working Memory",
      "Semantic Fluency",
      "Memory of Sound Pattern",
      "Visualization",
      "Rate of TEST Taking",
    ];

    return {
      filename: "assessment_template.xlsx",
     headers: [
    "Voucher",
    "Nama",
    "Gender",
    "Tanggal Lahir",
    "Kelas",
    "Tahun Ajaran",
    "Sekolah",
    ...metricHeaders,
  ],
  sampleRow: [
    "VCTEST1",
    "Saputra Andri",
    "Laki-laki",
    "2013/05/21",
    "8A",
    "2024/2025",
    "Sekolah A",
    ...metricHeaders.map(() => ""),
  ],

    };
  }, []);

  /* ===========================
   * Render
   * =========================*/
  return (
    <div className="space-y-6">
      {/* HEADER + ACTIONS */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Manajemen Asesmen</h2>
          <p className="text-gray-600 mt-1">Kelola data hasil asesmen siswa</p>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setShowBulkImport(true)}
            disabled={importing}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              importing ? "bg-green-400 text-white cursor-not-allowed" : "bg-green-600 text-white hover:bg-green-700"
            }`}
          >
            <Download className="w-4 h-4" />
            {importing ? "Mengimpor..." : "Import Excel/CSV"}
          </button>

          <button
            type="button"
            onClick={canExport ? handleExport : undefined}
            disabled={!canExport}
            title={exportTooltip}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors font-medium ${
              canExport ? "bg-gray-700 text-white hover:bg-gray-800" : "bg-gray-200 text-gray-400 cursor-not-allowed"
            }`}
          >
            <Upload className="w-4 h-4" />
            Export Excel
          </button>

          <button
            onClick={() => {
              setEditingResult(null);
              setShowForm(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-[#4F7CFF] text-white rounded-lg hover:bg-[#3D6AEE] transition-colors font-medium"
          >
            <Plus className="w-4 h-4" />
            Tambah Asesmen
          </button>
        </div>
      </div>

      {/* FILTER BAR */}
      <div className="flex items-end gap-4">
        <div className="w-full max-w-sm">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Filter berdasarkan Distributor
          </label>

         <div className="relative" ref={distributorDropdownRef}>
          <button
            type="button"
            onClick={() => setDistributorOpen((v) => !v)}
            className="relative w-full px-4 pr-10 py-2.5 border border-gray-300 rounded-lg bg-white text-left
                      focus:outline-none focus:ring-2 focus:ring-[#4F7CFF] focus:border-transparent transition-all"
          >
            {filterDistributor ? filterDistributor : "Semua Distributor"}

            <ChevronDown
              className={`pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500 transition-transform ${
                distributorOpen ? "rotate-180" : ""
              }`}
            />
          </button>


  {distributorOpen && (
    <div className="absolute z-50 mt-2 w-full rounded-lg border border-gray-200 bg-white shadow-lg overflow-hidden">
      <div className="max-h-64 overflow-y-auto">
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            setFilterDistributor("");
            setPage(1);
            setDistributorOpen(false);
          }}
          className="w-full text-left px-4 py-2.5 text-sm text-gray-800 hover:bg-gray-50 transition-colors"
        >
          Semua Distributor
        </button>
        {uniqueDistributors.map((d) => (
          <button
            key={d}
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              setFilterDistributor(d);
              setPage(1);
              setDistributorOpen(false);
            }}
            className="w-full text-left px-4 py-2.5 text-sm text-gray-800 hover:bg-gray-50 transition-colors"
          >
            {d}
          </button>
        ))}
      </div>
    </div>
  )}
</div>

        </div>

        {/* Sort toggle (same height as select) */}
        <button
          type="button"
          onClick={() => setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"))}
          className="h-11 px-4 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white
                     hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#4F7CFF]
                     focus:border-transparent transition-all"
        >
          Urutkan: {sortOrder === "desc" ? "Terbaru" : "Terlama"}
        </button>
      </div>

      {/* TABEL */}
      <DataTable
        data={filteredResults}
        columns={columns}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onBulkDelete={handleBulkDelete}
        searchPlaceholder="Cari hasil asesmen..."
        loading={loading}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(n) => {
          setPageSize(n);
          setPage(1);
        }}
        onSearchChange={(q) => {
          setTableSearch(q);
          setPage(1);
        }}
        searchValue={tableSearch}
      />

      {showForm && (
        <AssessmentResultForm
          result={editingResult}
          onClose={handleFormClose}
          onSaved={handleSaved}
        />
      )}

      {viewingResult && (
        <AssessmentResultView result={viewingResult} onClose={handleViewClose} />
      )}

      <BulkAssessmentImportSummaryModal
        open={showImportSummary}
        onClose={() => setShowImportSummary(false)}
        summary={importSummary}
      />

      <BulkImportModal
        open={showBulkImport}
        title="Import Hasil Asesmen"
        subtitle='Upload file Excel/CSV sesuai template. "Distributor" otomatis dari pemilik voucher. Kolom "Sekolah" hanya untuk lokasi asesmen (opsional).'
        accept=".csv,.xlsx,.xls"
        template={assessmentTemplate}
        uploading={importing}
        onClose={() => !importing && setShowBulkImport(false)}
        onUpload={handleBulkUploadFile}
      />

      <DeleteConfirmModal
        open={deleteModal.open}
        title={deleteModal.title}
        message={deleteModal.message}
        tone={deleteModal.tone}
        confirmText={deleteModal.confirmText}
        cancelText={deleteModal.cancelText}
        onCancel={() => closeDeleteModal(false)}
        onConfirm={() => closeDeleteModal(true)}
      />
    </div>
  );
}
